// tienda/HeroSection.jsx
// Hero with copy on the left and the live-tracking dashboard mockup on the right.

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, HandCoins, MessageSquare, Truck, ArrowRight, Zap } from "lucide-react";
import { brand, WA_HREF, fadeUp } from "../shared/brand";
import { MapMockup } from "./MapMockup";

export function HeroSection() {
  return (
    <section className="bg-white border-b border-slate-100">
      <div className="mx-auto max-w-7xl px-4 pt-12 pb-14 md:pt-16 md:pb-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">

          {/* Left: copy */}
          <motion.div {...fadeUp}>
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold text-white mb-5"
              style={{ background: brand.accentGreen }}
            >
              <Zap className="size-3" />
              El courier más económico de Lima — desde S/ 8.90
            </div>

            <h1
              className="text-3xl md:text-5xl font-extrabold leading-tight"
              style={{ color: brand.primary }}
            >
              La logística inteligente que impulsa tu negocio online en Lima
            </h1>

            <p className="mt-4 text-base text-slate-600 max-w-xl">
              Entregas el mismo día con seguimiento en vivo, notificaciones automáticas
              y <strong>cobro contraentrega</strong> confiable. Sube tu Excel, nosotros
              nos encargamos del resto.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5">
                <Clock className="size-4" /> Corte 12:00 PM → Entrega hasta 8:00 PM
              </span>
              <span className="flex items-center gap-1.5">
                <HandCoins className="size-4" /> Contraentrega segura
              </span>
              <span className="flex items-center gap-1.5">
                <MessageSquare className="size-4" /> WhatsApp automático
              </span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-sm"
                style={{ background: brand.primary }}
              >
                Crea tu cuenta gratis
                <ArrowRight className="size-4" />
              </Link>
              <a
                href={WA_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Agendar mi primera entrega
              </a>
            </div>
          </motion.div>

          {/* Right: dashboard mockup */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
            <div className="relative rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <span className="text-sm font-semibold">Panel de seguimiento</span>
                <span className="text-xs text-slate-500">Tiempo real</span>
              </div>
              <div className="grid md:grid-cols-5">
                <div className="md:col-span-2 border-r border-slate-200 p-4 space-y-3">
                  {["Maria-000231", "Pedro-000232", "Ricardo-000233", "Jimena-000234"].map(code => (
                    <div
                      key={code}
                      className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Truck className="size-4" style={{ color: brand.primary }} />
                        <span className="text-xs">{code}</span>
                      </div>
                      <span
                        className="rounded-md px-2 py-0.5 text-xs font-semibold text-white"
                        style={{ background: brand.accentGreen }}
                      >
                        En ruta
                      </span>
                    </div>
                  ))}
                </div>
                <div className="md:col-span-3 relative min-h-52 bg-slate-50">
                  <MapMockup />
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}